import { FC, useEffect, useRef, useState } from 'react';

import { ITraderWallet, useLazyGetTraderWalletStatementQuery } from '@entities/trades/wallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Tooltip } from '@mui/material';
import ErrorMessage from '@shared/components/ErrorMessage';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { useModal } from '@shared/components/Modal';

interface IProps {
  wallet: ITraderWallet;
}

const FILE_EXTENSIONS: Record<string, string> = {
  'text/csv': '.csv',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
};

const StatementPreview: FC<{ id: string }> = ({ id }) => {
  const [getStatement, { isLoading, isError, error, isSuccess }] =
    useLazyGetTraderWalletStatementQuery();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const blob = await getStatement(id).unwrap();
        const nextUrl = URL.createObjectURL(blob);
        setFileType(blob.type || 'application/octet-stream');
        setFileUrl(nextUrl);
        urlRef.current = nextUrl;
      } catch {}
    };

    load();

    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [id, getStatement]);

  return (
    <>
      <h2>Verification statement</h2>

      {isError && <ErrorMessage error={error} />}
      {isLoading && <LoadingSpinner />}

      {isSuccess && fileType === 'application/pdf' && (
        <iframe src={fileUrl ?? ''} width='100%' height='800' title='Verification statement' />
      )}

      {isSuccess && fileType && fileType !== 'application/pdf' && (
        <div>
          Preview is not available for this file type.
          <div>
            <a
              href={fileUrl ?? ''}
              download={`wallet-${id}-statement${FILE_EXTENSIONS[fileType] ?? ''}`}
            >
              Download statement
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export const WalletStatement: FC<IProps> = ({ wallet }) => {
  const { openModal } = useModal();

  const handleOpen = () => {
    openModal(<StatementPreview id={wallet.id} />, 'lg');
  };

  if (wallet.status !== 'verification' || !wallet.has_verification_statement) return null;

  return (
    <Tooltip title='View verification statement'>
      <ReceiptIcon sx={{ color: '#1976d2', cursor: 'pointer' }} onClick={handleOpen} />
    </Tooltip>
  );
};
